import { FC, ReactNode, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Unstable_Grid2";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import SvgIcon from "@mui/material/SvgIcon";
import Typography from "@mui/material/Typography";

import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import DirectionsCarOutlinedIcon from "@mui/icons-material/DirectionsCarOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import BeachAccessOutlinedIcon from "@mui/icons-material/BeachAccessOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import MedicationOutlined from "@mui/icons-material/MedicationOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import PhoneAndroidOutlinedIcon from "@mui/icons-material/PhoneAndroidOutlined";
import AutoStoriesOutlinedIcon from "@mui/icons-material/AutoStoriesOutlined";

import { Seo } from "src/components/seo";
import { usePageView } from "src/hooks/use-page-view";
import { paths } from "src/paths";
import useUserDataProvider from "src/contexts/userData-context";
import useHousehold from "src/contexts/household-context";
import { useProgress } from "src/contexts/progress-context";
import {
  useHouseholdModuleCounts,
  type MemberRef,
} from "src/sections/dashboard/home/use-household-module-counts";
import type { DocRow, DocRowStatus } from "src/utils/document-rows";
import type {
  Asset,
  AssetTransferPlan,
  PropertyDeed,
} from "src/types/snugtotal";
import {
  GenericPath,
  useV3GenericGetData,
} from "src/api/snugtotal/estate_app";
import { STATUS_TONES } from "src/components/status-dot-chip";
import {
  currentPropertyDeed,
  primaryTransferPlan,
  transferChipState,
} from "src/utils/asset-transfer";

/** Fixed height of the scrollable mini-list so both hero cards match in size. */
const LIST_HEIGHT = 196;

const CARD_SX = {
  height: "100%",
  border: "1px solid #e7e7e7",
  borderRadius: 1,
  backgroundColor: "#fff",
  cursor: "pointer",
  transition: "border-color 120ms ease, background-color 120ms ease",
  "&:hover": { backgroundColor: "#FAF9F8", borderColor: "#d9d9d9" },
} as const;

const DOC_STATUS: Record<
  DocRowStatus,
  { label: string; bg: string; dot: string }
> = {
  signed: { label: "Signed", bg: "#e8f5e9", dot: "#43a047" },
  ready: { label: "Ready to Sign", bg: "#e3f2fd", dot: "#1e88e5" },
  generating: { label: "Generating", bg: "#fff7e6", dot: "#fb8c00" },
  in_progress: { label: "In Progress", bg: "#e3f2fd", dot: "#1e88e5" },
  not_started: { label: "Not Started", bg: "#f5f5f5", dot: "#9e9e9e" },
};

/* ---- small shared pieces ------------------------------------------------- */

const IconBadge: FC<{ children: ReactNode }> = ({ children }) => (
  <Box
    sx={{
      p: 1,
      borderRadius: 1,
      backgroundColor: "primary.alpha12",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <SvgIcon sx={{ color: "primary.main" }} fontSize='small'>{children}</SvgIcon>
  </Box>
);

const Eyebrow: FC<{ children: ReactNode }> = ({ children }) => (
  <Typography
    variant="h5"
  >
    {children}
  </Typography>
);

/** Compact dot + label pill shared by both hero cards' rows. */
const StatusPill: FC<{ label: string; bg: string; dot: string }> = ({
  label,
  bg,
  dot,
}) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={0.75}
    sx={{
      backgroundColor: bg,
      borderRadius: 100,
      px: 1,
      py: "3px",
      flexShrink: 0,
    }}
  >
    <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: dot }} />
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 600,
        color: "rgba(0,0,0,0.72)",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Typography>
  </Stack>
);

const DocStatusPill: FC<{ status: DocRowStatus }> = ({ status }) => (
  <StatusPill {...DOC_STATUS[status]} />
);

/** The Assets page transfer-at-death pill, sized down to match the doc pill. */
const TransferPill: FC<{
  asset: Asset;
  plans: AssetTransferPlan[];
  deeds: PropertyDeed[];
}> = ({ asset, plans, deeds }) => {
  const chip = transferChipState(
    primaryTransferPlan(plans, asset.id),
    currentPropertyDeed(deeds, asset.id)
  );
  const tone = STATUS_TONES[chip.kind];
  return <StatusPill label={chip.label} bg={tone.bg} dot={tone.dot} />;
};

function stringToColor(value: string): string {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = value.charCodeAt(i) + ((hash << 5) - hash);
  }
  const toHex = (n: number) =>
    Math.min(180, Math.max(60, n & 0xff))
      .toString(16)
      .padStart(2, "0");
  return `#${toHex(hash >> 16)}${toHex(hash >> 8)}${toHex(hash)}`;
}

const initialsOf = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return (parts[0].slice(0, 2) || "?").toUpperCase();
  return `${parts[0][0] ?? ""}${parts[parts.length - 1][0] ?? ""}`.toUpperCase();
};

const PersonAvatar: FC<{ name: string }> = ({ name }) => (
  <Avatar
    sx={{
      width: 20,
      height: 20,
      fontSize: 9,
      fontWeight: 600,
      bgcolor: stringToColor(name || "Member"),
      color: "#fff",
    }}
  >
    {initialsOf(name || "M")}
  </Avatar>
);

const assetIcon = (type?: string): ReactNode => {
  switch (type) {
    case "realestate":
      return <HomeOutlinedIcon />;
    case "financial_account":
      return <SavingsOutlinedIcon />;
    case "business_interest":
      return <BusinessOutlinedIcon />;
    case "personal_property":
      return <DirectionsCarOutlinedIcon />;
    case "life_insurance":
      return <BeachAccessOutlinedIcon />;
    default:
      return <AccountBalanceWalletOutlinedIcon />;
  }
};

/** Mirror the asset naming used by the assets section (no single title field). */
const assetLabel = (a: Asset): string =>
  a.institution ||
  a.address ||
  a.business_name ||
  a.coverage_institution ||
  (a.asset_type === "personal_property" ? a.notes : "") ||
  "Unnamed asset";

const formatCurrency = (n?: number | null): string =>
  typeof n === "number" && !Number.isNaN(n)
    ? n.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      })
    : "";

const RowShell: FC<{ children: ReactNode }> = ({ children }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1.5}
    sx={{ py: 1, minWidth: 0 }}
  >
    {children}
  </Stack>
);

/** Light gray count pill, matching the side nav counts (NavCount in layouts/dashboard/config.tsx). */
const CountPill: FC<{ count: number; loaded: boolean }> = ({
  count,
  loaded,
}) => (
  <Box
    component="span"
    sx={{
      flexShrink: 0,
      minWidth: 22,
      textAlign: "center",
      borderRadius: 100,
      px: 1,
      py: "2px",
      fontSize: 12,
      fontWeight: 600,
      lineHeight: 1.5,
      backgroundColor: "#ededed",
      color: "#8a8a8a",
    }}
  >
    {loaded ? count : "—"}
  </Box>
);

const EmptyHint: FC<{ children: ReactNode }> = ({ children }) => (
  <Box sx={{ py: 3, textAlign: "center" }}>
    <Typography variant="body2" sx={{ color: "text.secondary" }}>
      {children}
    </Typography>
  </Box>
);

/* ---- hero card shell ----------------------------------------------------- */

const HeroCard: FC<{
  icon: ReactNode;
  eyebrow: string;
  caption: string;
  list: ReactNode;
  footer: ReactNode;
  onClick: () => void;
}> = ({ icon, eyebrow, caption, list, footer, onClick }) => (
  <Card
    variant="outlined"
    sx={{ ...CARD_SX, display: "flex", flexDirection: "column" }}
    onClick={onClick}
  >
    <Box sx={{ p: 2.5, pb: 1.5 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <IconBadge>{icon}</IconBadge>
          <Box>
            <Eyebrow>{eyebrow}</Eyebrow>
            
          </Box>
        </Stack>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {caption}
            </Typography>
      </Stack>
    </Box>

    <Divider sx={{ borderColor: "#eee" }} />

    <Box
      sx={{
        px: 2.5,
        height: LIST_HEIGHT,
        overflowY: "auto",
        // Scrollable, but keep the scrollbar hidden for a clean edge.
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        "&::-webkit-scrollbar": { display: "none" },
      }}
    >
      {list}
    </Box>

    {footer && (
      <>
        <Divider sx={{ borderColor: "#f0f0f0" }} />
        <Box sx={{ p: 2.5, pt: 1.75 }}>{footer}</Box>
      </>
    )}
  </Card>
);

/* ---- module grid --------------------------------------------------------- */

interface ModuleDef {
  key: keyof ReturnType<typeof useProgress>["modules"];
  title: string;
  description: string;
  icon: ReactNode;
  to: (udId: string) => string;
}

const MODULES: ModuleDef[] = [
  {
    key: "personal",
    title: "Personal",
    description: "Contacts, pets, advisors, and personal history.",
    icon: <PeopleOutlineIcon />,
    to: paths.dashboard.personal,
  },
  {
    key: "legal",
    title: "Legal",
    description: "Estate, marriage, and business documents.",
    icon: <FolderOutlinedIcon />,
    to: paths.dashboard.legal,
  },
  {
    key: "medical",
    title: "Medical",
    description: "Contacts, history, allergies, and medications.",
    icon: <MedicationOutlined />,
    to: paths.dashboard.medical,
  },
  {
    key: "financial",
    title: "Financial",
    description: "Assets, debts, and insurance in one place.",
    icon: <AccountBalanceOutlinedIcon />,
    to: paths.dashboard.financial,
  },
  {
    key: "property",
    title: "Property",
    description: "Vehicles, real estate, and personal property.",
    icon: <ApartmentOutlinedIcon />,
    to: paths.dashboard.property,
  },
  {
    key: "digital",
    title: "Digital",
    description: "Online accounts and digital assets.",
    icon: <PhoneAndroidOutlinedIcon />,
    to: paths.dashboard.digital,
  },
  {
    key: "legacy",
    title: "Legacy",
    description: "Family history and words of wisdom to pass on.",
    icon: <AutoStoriesOutlinedIcon />,
    to: paths.dashboard.legacy,
  },
];

const firstName = (full?: string | null): string =>
  full ? full.trim().split(/\s+/)[0] : "";

/* ========================================================================= */

export const HomeV2: FC = () => {
  const navigate = useNavigate();
  const { userData } = useUserDataProvider();
  const { selectedHousehold } = useHousehold();
  const progress = useProgress();
  usePageView();

  const udId =
    progress.udId || selectedHousehold?.head_of_household?.ud_id || "";
  const greetingName = firstName(
    userData?.full_name || selectedHousehold?.head_of_household?.full_name
  );

  const { documents, assets } = progress;

  // Transfer plans and deeds drive the per-asset pill, scoped to the same
  // member whose assets the progress context lists.
  const fetchTransferPlans = useV3GenericGetData<AssetTransferPlan[]>(
    GenericPath.GENERIC_ENTITY_LIST_CREATE_V3
  );
  const fetchPropertyDeeds = useV3GenericGetData<PropertyDeed[]>(
    GenericPath.GENERIC_ENTITY_LIST_CREATE_V3
  );
  const [transferPlans, setTransferPlans] = useState<AssetTransferPlan[]>([]);
  const [propertyDeeds, setPropertyDeeds] = useState<PropertyDeed[]>([]);
  useEffect(() => {
    if (!udId) return;
    let cancelled = false;
    Promise.all([
      fetchTransferPlans({ ud_id: udId, entity: "asset-transfer-plans" }).catch(
        (err) => {
          console.error(err);
          return [] as AssetTransferPlan[];
        }
      ),
      fetchPropertyDeeds({ ud_id: udId, entity: "property-deeds" }).catch(
        (err) => {
          console.error(err);
          return [] as PropertyDeed[];
        }
      ),
    ]).then(([plans, deeds]) => {
      if (cancelled) return;
      setTransferPlans(plans ?? []);
      setPropertyDeeds(deeds ?? []);
    });
    return () => {
      cancelled = true;
    };
  }, [udId, fetchTransferPlans, fetchPropertyDeeds]);

  // Everyone in the plan (head + household members), matching the member switch.
  const members = useMemo<MemberRef[]>(() => {
    const list: MemberRef[] = [];
    const head = selectedHousehold?.head_of_household;
    if (head?.ud_id) {
      list.push({ udId: head.ud_id, name: head.full_name || "Head of Household" });
    }
    (selectedHousehold?.household_member_roles ?? [])
      .filter((m) => m.is_household_member)
      .forEach((m) => {
        if (m.role_target_user_data_ud_id) {
          list.push({
            udId: m.role_target_user_data_ud_id,
            name: m.role_target_user_data?.full_name || "Member",
          });
        }
      });
    return list.filter(
      (m, i) => list.findIndex((o) => o.udId === m.udId) === i
    );
  }, [selectedHousehold]);

  const { countsByUd, loaders } = useHouseholdModuleCounts(members);

  const docRows: DocRow[] = documents.items;
  const docBar =
    documents.total > 0 ? (documents.complete / documents.total) * 100 : 0;
  const docCaption =
    documents.total === 0
      ? "Nothing to sign yet"
      : documents.status === "complete"
      ? "All documents ready"
      : `${documents.complete} of ${documents.total} ready`;

  const assetItems: Asset[] = assets.items;
  const assetTotal = assetItems.reduce(
    (sum, a) => sum + (typeof a.value === "number" ? a.value : 0),
    0
  );
  const assetCaption =
    assets.count === 0
      ? "No assets yet"
      : `${assets.count} ${assets.count === 1 ? "asset" : "assets"} tracked`;

  return (
    <>
      <Seo title="Total Estate Planning" />
      <Box
        component="main"
        sx={{ flexGrow: 1, pb: 10, px: 0, backgroundColor: "#fcfcfc" }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          {/* Header */}
          <Stack
            spacing={0.75}
            sx={{ pt: { xs: 4, md: 6 }, pb: { xs: 3, md: 4 } }}
          >
            <Typography variant="h3">
              {greetingName ? `Welcome back, ${greetingName}` : "Welcome back"}
            </Typography>
            <Typography sx={{ mt: 2 }} variant="body2">
              A snapshot of your plan, ready for you to pick up where you left off.
            </Typography>
          </Stack>

          {/* Hero roll-up cards */}
          <Grid container spacing={{ xs: 2, md: 3 }} alignItems="stretch">
            <Grid xs={12} md={6}>
              <HeroCard
                icon={<FolderOutlinedIcon />}
                eyebrow="Documents"
                caption={docCaption}
                onClick={() => navigate(paths.dashboard.planning.documents)}
                list={
                  docRows.length === 0 ? (
                    <EmptyHint>No documents to show yet.</EmptyHint>
                  ) : (
                    <Stack divider={<Divider sx={{ borderColor: "#f4f4f4" }} />}>
                      {docRows.map((row) => (
                        <RowShell key={row.id}>
                          <PersonAvatar name={row.personName} />
                          <Typography
                            variant="body2"
                            sx={{
                              flexGrow: 1,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {row.title}
                          </Typography>
                          <DocStatusPill status={row.status} />
                        </RowShell>
                      ))}
                    </Stack>
                  )
                }
                footer={
                  <Stack spacing={1}>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary", fontWeight: 500 }}
                    >
                      {documents.total > 0
                        ? `${Math.round(docBar)}% complete`
                        : "Get started"}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={docBar}
                      sx={{
                        height: 6,
                        borderRadius: 100,
                        backgroundColor: "#eeeeee",
                        "& .MuiLinearProgress-bar": { borderRadius: 100 },
                      }}
                    />
                  </Stack>
                }
              />
            </Grid>

            <Grid xs={12} md={6}>
              <HeroCard
                icon={<AccountBalanceOutlinedIcon />}
                eyebrow="Assets"
                caption={assetCaption}
                onClick={() => navigate(paths.dashboard.planning.assets)}
                list={
                  assetItems.length === 0 ? (
                    <EmptyHint>No assets added yet.</EmptyHint>
                  ) : (
                    <Stack divider={<Divider sx={{ borderColor: "#f4f4f4" }} />}>
                      {assetItems.map((a) => (
                        <RowShell key={a.id}>
                          <Box
                            sx={{
                              width: 26,
                              height: 26,
                              borderRadius: 1,
                              border: "1px solid #eee",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <SvgIcon
                              sx={{ fontSize: 16, color: "text.secondary" }}
                            >
                              {assetIcon(a.asset_type as string)}
                            </SvgIcon>
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              flexGrow: 1,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {assetLabel(a)}
                          </Typography>
                          <TransferPill
                            asset={a}
                            plans={transferPlans}
                            deeds={propertyDeeds}
                          />
                        </RowShell>
                      ))}
                    </Stack>
                  )
                }
                footer={
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", fontWeight: 500 }}
                  >
                    {assetTotal > 0
                      ? `Total value ${formatCurrency(assetTotal)}`
                      : "Add assets for tracking"}
                  </Typography>
                }
              />
            </Grid>
          </Grid>

          {/* Life Details */}
          <Stack
            direction="row"
            alignItems="baseline"
            justifyContent="space-between"
            sx={{ mt: { xs: 5, md: 7 } }}
          >
            <Typography variant="h4">
              Your life details
            </Typography>
          </Stack>
          <Typography sx={{ mt: 1, mb:4 }} variant="body2">
                        Organizing all of life's details in one place, to leave
                        a legacy and not a mess.
                      </Typography>

          <Grid container spacing={{ xs: 2, md: 3 }}>
            {MODULES.map((m) => {
              const anyLoaded = members.some((mem) => countsByUd[mem.udId]);
              const total = members.reduce(
                (sum, mem) => sum + (countsByUd[mem.udId]?.[m.key] ?? 0),
                0
              );
              return (
                <Grid xs={12} sm={6} md={4} key={m.key}>
                  <Card
                    variant="outlined"
                    sx={CARD_SX}
                    onClick={() => navigate(m.to(udId))}
                  >
                    <Box
                      sx={{
                        p: 2.5,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        height: "100%",
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1.25}
                      >
                        <Box
                          sx={{
                            width: 30,
                            height: 30,
                            borderRadius: 1,
                            backgroundColor: "primary.alpha12",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <SvgIcon
                            sx={{ fontSize: 18, color: "primary.main" }}
                          >
                            {m.icon}
                          </SvgIcon>
                        </Box>
                        <Typography variant="h6" sx={{ flexGrow: 1 }}>
                          {m.title}
                        </Typography>
                        <CountPill count={total} loaded={anyLoaded} />
                      </Stack>

                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary" }}
                      >
                        {m.description}
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {/* Off-screen loaders that fetch each member's module counts */}
          <Box sx={{ display: "none" }}>{loaders}</Box>
        </Container>
      </Box>
    </>
  );
};

export default HomeV2;
